/**
 * PEDIT-TREE — the project entity TREE for the edit Overview step.
 *
 * Replaces the parks→turbines list (which duplicated the Parks & Turbines step)
 * with a full, READ-ONLY directory-style tree of the whole project:
 *
 *   Project → Contract → Contract Position → Inspection Instruction → Data Acq.
 *
 * Each level is fetched for the project and linked client-side by parent FK
 * (`entityTreeModel.buildEntityTree`). Per the research (deep 5-level hierarchy
 * → collapsible tree, not a wide table) the tree is:
 *   - a directory-style collapsible tree with clear indentation; one entity per
 *     row: [twisty] [kind icon] code/name … [state chip];
 *   - Expand all / Collapse all control; default collapsed to ~Contract level;
 *   - DAs under an II are lazy-RENDERED (capped, with "show more") so a big
 *     project doesn't paint thousands of rows up front;
 *   - a per-node click context menu: "Show code + UUID" (UUID hidden by
 *     default) and "Go to <entity>" (navigates to the entity's existing route).
 *
 * The node renderer + per-kind metadata are generic so future kinds (Reports,
 * Issues, …) slot in additively.
 *
 * Tenant readiness: every query is `enabled`-gated on session+tenant being
 * resolved (mirrors `ProjectParksTree` / `reference_tenant_scoped_query_
 * enabled_gate`) so none fire before the `X-Tenant-ID` header attaches.
 */
import { useMemo, useState, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { useAuthStore, useMe } from "@aeriosiq/auth-client";
// DL-01 — "Download images" on a data set, an instruction (all its sets) and
// the project (owner roles only) from the node's context menu.
import {
  DownloadImagesDialog,
  canDownloadImages,
  needsCustomerFlag,
  useCustomerImageDownloadFlag,
  type DownloadScope,
  type DownloadScopeKind,
} from "@aeriosiq/lyse";
import { useContractsListQuery } from "../../contracts/useContracts.js";
import { useContractPositionsListQuery } from "../../contract-positions/useContractPositions.js";
import { useInspectionInstructionsListQuery } from "../../inspection-instructions/useInspectionInstructions.js";
import { useProjectDetailQuery } from "../useProjects.js";
import { EntityStateChip } from "./entityTreeState.js";
import {
  buildEntityTree,
  countSummary,
  KIND_META,
  type ContractRow,
  type DaRow,
  type EntityKind,
  type EntityNode,
  type IiRow,
  type PositionRow,
} from "./entityTreeModel.js";
import { useDataAcquisitionsForProject } from "./useDataAcquisitionsForProject.js";
import {
  CameraIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronsDownUpIcon,
  ChevronsUpDownIcon,
  ClipboardListIcon,
  FileTextIcon,
  FolderIcon,
  WindIcon,
} from "@aeriosiq/ui/icons";

const PAGE_SIZE = 500;
// Lazy-render cap: only this many DA rows under an II are painted at first;
// the rest reveal on "show more" so a big project stays responsive.
const DA_RENDER_CAP = 25;

const KIND_ICON: Record<EntityKind, ReactElement> = {
  project: <FolderIcon fontSize="small" />,
  contract: <FileTextIcon fontSize="small" />,
  contractPosition: <WindIcon fontSize="small" />,
  inspectionInstruction: <ClipboardListIcon fontSize="small" />,
  dataAcquisition: <CameraIcon fontSize="small" />,
};

/** DL-01 — what a node's "Download images" needs to decide its visibility. */
export interface DownloadContext {
  roles: readonly string[] | undefined;
  ownership: "owner" | "member";
  accessRole: string | null;
  /** the owner's flag, read for a customer only (null = unknown / not asked) */
  customerDownloadEnabled: boolean | null;
}

const DOWNLOAD_KIND: Partial<Record<EntityKind, DownloadScopeKind>> = {
  project: "project",
  inspectionInstruction: "instruction",
  dataAcquisition: "da",
};

export function ProjectEntityTree({
  projectId,
  ownership = "owner",
  accessRole = null,
}: {
  projectId: string;
  /** DL-01 — the project's side of the fence (the list row's `ownership`). */
  ownership?: "owner" | "member" | undefined;
  /** DL-01 — the caller's access role on a member project (the row's `role`). */
  accessRole?: string | null | undefined;
}): ReactElement {
  const session = useAuthStore((s) => s.session);
  const tenant = useAuthStore((s) => s.tenant);
  const tenantReady =
    !!session && !!tenant && session.tenant.tenantSlug === tenant.tenantSlug;
  const enabled = tenantReady && projectId.length > 0;

  const detail = useProjectDetailQuery(enabled ? projectId : null);

  // DL-01 — who may download: the owner roles, or a customer with the
  // owner's flag on (read once per project, only for a customer)
  const me = useMe();
  const customerFlag = useCustomerImageDownloadFlag(
    projectId,
    enabled && needsCustomerFlag({ ownership, accessRole, scopeKind: "da" }),
  );
  const download: DownloadContext = {
    roles: me.data?.roles,
    ownership,
    accessRole,
    customerDownloadEnabled: customerFlag,
  };

  const contractsQ = useContractsListQuery({
    projectIds: enabled ? [projectId] : [],
    includeCompleted: true,
    page: 1,
    pageSize: PAGE_SIZE,
  });
  const positionsQ = useContractPositionsListQuery(
    {
      projectIds: enabled ? [projectId] : [],
      includeCompleted: true,
      page: 1,
      pageSize: PAGE_SIZE,
    },
    { enabled },
  );
  const iisQ = useInspectionInstructionsListQuery({
    projectIds: enabled ? [projectId] : [],
    showCompleted: true,
    page: 1,
    pageSize: PAGE_SIZE,
  });
  const dasQ = useDataAcquisitionsForProject(enabled ? projectId : null);

  const isPending =
    detail.isPending ||
    contractsQ.isPending ||
    positionsQ.isPending ||
    iisQ.isPending ||
    dasQ.isPending;
  const isError =
    detail.isError ||
    contractsQ.isError ||
    positionsQ.isError ||
    iisQ.isError ||
    dasQ.isError;

  const tree = useMemo<EntityNode | null>(() => {
    if (!detail.data) return null;
    const contracts: ContractRow[] = (contractsQ.data?.items ?? []).map(
      (c) => ({
        contractId: c.contractId,
        contractCode: c.contractCode,
        projectId: c.projectId,
        state: c.state,
      }),
    );
    const positions: PositionRow[] = (positionsQ.data?.items ?? []).map(
      (p) => ({
        contractPositionId: p.contractPositionId,
        positionCode: p.positionCode,
        assetDisplayLabel: p.assetDisplayLabel,
        contractId: p.contractId ?? null,
        state: p.state,
      }),
    );
    // contractPositionId is nullable since projects/v1@2.22.0 (orphan /
    // field-created IIs). This tree nests IIs under their contract position, so
    // an II with no position has no anchor here — drop it (an orphan also has no
    // project, so it wouldn't be in a project's II set in the first place).
    const iis: IiRow[] = (iisQ.data?.items ?? [])
      .filter(
        (ii): ii is typeof ii & { contractPositionId: string } =>
          ii.contractPositionId !== null,
      )
      .map((ii) => ({
        inspectionInstructionId: ii.inspectionInstructionId,
        instructionCode: ii.instructionCode,
        contractPositionId: ii.contractPositionId,
        state: ii.state,
      }));
    const das: DaRow[] = (dasQ.data?.items ?? []).map((d) => ({
      id: d.id,
      instructionCode: d.instructionCode,
      inspectionInstructionId: d.inspectionInstructionId ?? null,
      state: d.state,
    }));
    return buildEntityTree({
      project: {
        projectId: detail.data.projectId,
        projectCode: detail.data.projectCode,
        projectName: detail.data.projectName,
      },
      contracts,
      positions,
      iis,
      das,
    });
  }, [
    detail.data,
    contractsQ.data?.items,
    positionsQ.data?.items,
    iisQ.data?.items,
    dasQ.data?.items,
  ]);

  // Expansion set — default expands the project + its contracts (≈ collapse to
  // Contract level), so the PM sees the parks/contracts without thousands of
  // descendant rows up front.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (tree && seededFor !== tree.id) {
    const next = new Set<string>([tree.id]);
    for (const c of tree.children) next.add(c.id);
    setExpanded(next);
    setSeededFor(tree.id);
  }

  const allIds = useMemo<string[]>(
    () => (tree ? collectExpandableIds(tree) : []),
    [tree],
  );

  function expandAll(): void {
    setExpanded(new Set(allIds));
  }
  function collapseAll(): void {
    // Keep the root open so the tree never collapses to nothing.
    setExpanded(tree ? new Set([tree.id]) : new Set());
  }
  function toggle(id: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (isPending) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", py: 4 }}
        data-testid="project-entity-tree-loading"
      >
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (isError || !tree) {
    return (
      <Alert
        severity="error"
        variant="outlined"
        data-testid="project-entity-tree-error"
      >
        Couldn&apos;t load this project&apos;s entity tree.
      </Alert>
    );
  }

  return (
    <Stack spacing={1} data-testid="project-entity-tree">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {countSummary(tree) || "No contracts attached yet."}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<ChevronsUpDownIcon fontSize="small" />}
            onClick={expandAll}
          >
            Expand all
          </Button>
          <Button
            size="small"
            startIcon={<ChevronsDownUpIcon fontSize="small" />}
            onClick={collapseAll}
          >
            Collapse all
          </Button>
        </Stack>
      </Box>
      <Box
        role="tree"
        aria-label="Project entity tree"
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: "background.paper",
          py: 0.5,
        }}
      >
        <EntityRow
          node={tree}
          depth={0}
          expanded={expanded}
          onToggle={toggle}
          download={download}
        />
      </Box>
    </Stack>
  );
}

/* ── recursive row ───────────────────────────────────────────────────── */

function EntityRow({
  node,
  depth,
  expanded,
  onToggle,
  download,
}: {
  node: EntityNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  download: DownloadContext;
}): ReactElement {
  const hasChildren = node.children.length > 0;
  // DL-01 — "Download images" for a real DA / instruction / project node the
  // caller may download (owner roles everywhere; a customer on DA and
  // instruction only, with the owner's flag) — omitted otherwise.
  const downloadKind = node.synthetic ? undefined : DOWNLOAD_KIND[node.kind];
  const downloadScope: DownloadScope | null =
    downloadKind !== undefined &&
    node.uuid !== null &&
    canDownloadImages({
      roles: download.roles,
      ownership: download.ownership,
      accessRole: download.accessRole,
      customerDownloadEnabled: download.customerDownloadEnabled,
      scopeKind: downloadKind,
    })
      ? { kind: downloadKind, id: node.uuid, label: node.label }
      : null;
  const [downloading, setDownloading] = useState<DownloadScope | null>(null);
  const isOpen = expanded.has(node.id);
  const summary = countSummary(node);

  // Lazy render of the (often huge) DA layer: cap the painted children.
  const [shownCount, setShownCount] = useState(DA_RENDER_CAP);
  const childrenAreDas =
    node.kind === "inspectionInstruction" ||
    (node.synthetic && node.children[0]?.kind === "dataAcquisition");
  const visibleChildren =
    childrenAreDas && node.children.length > shownCount
      ? node.children.slice(0, shownCount)
      : node.children;
  const hiddenCount = node.children.length - visibleChildren.length;

  // Context menu (per-node lookup + navigate).
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [revealUuid, setRevealUuid] = useState(false);
  const meta = KIND_META[node.kind];
  // Resolve the "Go to" target eagerly to a string|null — a null target ⇒ the
  // action is omitted (synthetic bucket, missing uuid, or no route for the
  // kind). Computing the href here keeps the JSX free of flow-narrowing the
  // route fn (which eslint would flag as always-truthy).
  const goToHref =
    !node.synthetic && node.uuid !== null && meta.route !== null
      ? meta.route(node.uuid)
      : null;

  return (
    <Box role="treeitem" aria-expanded={hasChildren ? isOpen : undefined}>
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          gap: 1,
          pr: 1,
          pl: `${String(depth * 20 + 8)}px`,
          py: 0.5,
          cursor: "default",
          // From the JS palette: `var(--mui-palette-divider)` is never emitted
          // by a plain `createTheme()`, so this border fell back to
          // `currentColor` and drew the indent guide in body-text colour
          // instead of the divider tone (SB6-G2 Bug-7, same class).
          borderLeft: depth > 0 ? `1px solid ${theme.palette.divider}` : "none",
          "&:hover": { bgcolor: "action.hover" },
        })}
        data-testid="entity-tree-row"
        data-kind={node.kind}
        onClick={(e) => {
          // Click anywhere on the row (not the twisty) opens the context menu.
          setAnchor(e.currentTarget);
        }}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            aria-label={
              isOpen ? `Collapse ${node.label}` : `Expand ${node.label}`
            }
            onClick={(e) => {
              e.stopPropagation();
              onToggle(node.id);
            }}
            sx={{ p: 0.25 }}
          >
            {isOpen ? (
              <ChevronDownIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
          </IconButton>
        ) : (
          <Box sx={{ width: 24 }} />
        )}
        <Box sx={{ color: "text.secondary", display: "flex" }}>
          {KIND_ICON[node.kind]}
        </Box>
        <Typography
          variant="body2"
          noWrap
          title={node.label}
          sx={{ fontWeight: node.kind === "project" ? 600 : 400 }}
        >
          {node.label}
        </Typography>
        {revealUuid && node.uuid ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace" }}
            data-testid="entity-tree-uuid"
          >
            {node.uuid}
          </Typography>
        ) : null}
        {summary ? (
          <Typography variant="caption" color="text.secondary" noWrap>
            · {summary}
          </Typography>
        ) : null}
        <Box sx={{ flex: 1 }} />
        {node.state ? (
          <EntityStateChip kind={node.kind} state={node.state} />
        ) : null}
      </Box>

      {/* per-node context menu */}
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => {
          setAnchor(null);
        }}
      >
        <MenuItem
          onClick={() => {
            setRevealUuid((v) => !v);
            setAnchor(null);
          }}
          data-testid="entity-menu-show-id"
          disabled={node.uuid === null}
        >
          {revealUuid ? "Hide code + UUID" : "Show code + UUID"}
        </MenuItem>
        {goToHref !== null ? (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              void navigate(goToHref);
            }}
            data-testid="entity-menu-go-to"
          >
            Go to {meta.noun}
          </MenuItem>
        ) : null}
        {downloadScope !== null ? (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              setDownloading(downloadScope);
            }}
            data-testid="entity-menu-download-images"
          >
            {downloadScope.kind === "da"
              ? "Download images"
              : "Download images of all data sets"}
          </MenuItem>
        ) : null}
      </Menu>
      {downloadScope !== null ? (
        <DownloadImagesDialog
          scope={downloading}
          onClose={() => {
            setDownloading(null);
          }}
        />
      ) : null}

      {hasChildren ? (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <Box>
            {visibleChildren.map((child) => (
              <EntityRow
                key={child.id}
                node={child}
                depth={depth + 1}
                expanded={expanded}
                onToggle={onToggle}
                download={download}
              />
            ))}
            {hiddenCount > 0 ? (
              <Box sx={{ pl: `${String((depth + 1) * 20 + 32)}px`, py: 0.5 }}>
                <Button
                  size="small"
                  onClick={() => {
                    setShownCount((c) => c + DA_RENDER_CAP);
                  }}
                  data-testid="entity-tree-show-more"
                >
                  Show {Math.min(hiddenCount, DA_RENDER_CAP)} more
                </Button>
              </Box>
            ) : null}
          </Box>
        </Collapse>
      ) : null}
    </Box>
  );
}

/** All node ids that can be expanded (have children) — for "Expand all". */
function collectExpandableIds(node: EntityNode): string[] {
  const out: string[] = [];
  if (node.children.length > 0) {
    out.push(node.id);
    for (const c of node.children) out.push(...collectExpandableIds(c));
  }
  return out;
}
